import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import SEOMeta from "@/components/SEOMeta";
import Breadcrumbs from "@/components/Breadcrumbs";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import { Link } from "react-router-dom";
import { 
  Building, 
  Users, 
  Award, 
  Clock, 
  Phone,
  Mail,
  MapPin,
  Home,
  Wrench,
  HardHat,
  CheckCircle,
  Star,
  Shield
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";

// Import images
import torontoConstruction from "@/assets/toronto-construction.jpg";
import residentialConstruction from "@/assets/residential-construction.jpg";
import generalContractingProjects from "@/assets/general-contracting-projects.jpg";

const Toronto = () => {
  const cityData = {
    name: "Toronto",
    region: "Greater Toronto Area", 
    population: "2.9M",
    slug: "toronto-general-contracting",
    intro: "Ontario's largest city and economic center, Toronto is home to major commercial developments and residential projects. Blue Knight Contracting delivers expert general contracting and construction services across the GTA.",
    featured_services: [
      "Downtown Office Construction",
      "Condo Renovations", 
      "Commercial Build-outs",
      "Residential Custom Homes"
    ],
    nearby_areas: ["Mississauga", "Brampton", "Vaughan", "Markham", "Scarborough"],
    cta: "Request your Toronto construction quote today."
  };

  const faqData = [
    {
      question: "What types of construction projects do you handle in Toronto?",
      answer: "We handle all types of construction projects in Toronto including downtown office construction, condo renovations, commercial build-outs, residential custom homes, and high-rise developments across the GTA."
    },
    {
      question: "Do you have experience with Toronto building codes and permits?",
      answer: "Yes, our team has extensive experience with Toronto building codes, permit applications, and working with the City of Toronto's approval processes for both residential and commercial projects."
    },
    {
      question: "Can you handle large-scale commercial projects in downtown Toronto?",
      answer: "Absolutely. We specialize in large-scale commercial construction including office buildings, retail spaces, and mixed-use developments in downtown Toronto and throughout the GTA."
    },
    {
      question: "What areas of the Greater Toronto Area do you serve?",
      answer: "We serve all areas of Toronto and the GTA including Mississauga, Brampton, Vaughan, Markham, Scarborough, and surrounding municipalities in the Greater Toronto Area."
    },
    {
      question: "How long have you been providing construction services in Toronto?",
      answer: "Blue Knight Contracting has been providing professional construction and general contracting services in Toronto and the GTA for over 15 years, completing hundreds of successful projects."
    },
    {
      question: "Do you offer emergency construction services in Toronto?",
      answer: "Yes, we provide 24/7 emergency construction services throughout Toronto and the GTA for urgent repairs, storm damage, and emergency structural work."
    }
  ];

  const serviceData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Blue Knight Contracting Toronto",
    "description": "Professional general contracting and construction services in Toronto and the Greater Toronto Area. Specializing in commercial, residential, and industrial construction projects.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Toronto",
      "addressRegion": "Ontario", 
      "addressCountry": "Canada"
    },
    "telephone": "+1-647-760-8854",
    "url": "https://blueknightcontracting.ca/toronto",
    "areaServed": {
      "@type": "City",
      "name": "Toronto"
    },
    "serviceType": [
      "General Contracting Toronto",
      "Commercial Construction Toronto",
      "Residential Construction Toronto",
      "Condo Renovations Toronto",
      "Office Construction Toronto"
    ]
  };

  const constructionServices = [
    {
      title: "Downtown Office Construction",
      description: "Modern office buildings and corporate spaces in Toronto's financial district and downtown core.",
      icon: Building,
      features: [
        "High-rise office towers",
        "Corporate headquarters",
        "Modern workspace design",
        "LEED-certified buildings",
        "Tenant improvements",
        "Commercial fit-outs"
      ]
    },
    {
      title: "Condo Renovations",
      description: "Complete condo renovations and upgrades throughout Toronto's residential towers.",
      icon: Home,
      features: [
        "Full condo renovations",
        "Kitchen & bathroom remodels",
        "Open concept conversions",
        "Luxury finishes",
        "Space optimization",
        "Modern amenities"
      ]
    },
    {
      title: "Commercial Build-outs",
      description: "Retail, restaurant, and commercial space construction across Toronto.",
      icon: Wrench,
      features: [
        "Retail store construction",
        "Restaurant build-outs",
        "Medical office fit-outs",
        "Warehouse conversions",
        "Mixed-use developments",
        "Shopping center construction"
      ]
    },
    {
      title: "Residential Custom Homes",
      description: "Custom home construction and major renovations in Toronto neighborhoods.",
      icon: HardHat,
      features: [
        "Custom home builds",
        "Major home renovations",
        "Heritage home restoration",
        "Luxury residential projects",
        "Basement developments",
        "Home additions"
      ]
    }
  ];

  return (
    <>
      <SEOMeta 
        title="Toronto General Contractor | Blue Knight Contracting & Construction Toronto"
        description="Professional general contracting and construction services in Toronto and the GTA. Commercial, residential, and industrial construction projects. Licensed, insured, 20+ years experience."
        keywords="Toronto general contractor, Toronto construction company, GTA contracting, Toronto commercial construction, Toronto residential construction, downtown office construction, condo renovations Toronto"
        schemaType="LocalBusiness"
        schemaData={serviceData}
      />
      <LocalBusinessSchema 
        locationName="Toronto"
        addressLocality="Toronto"
        addressRegion="Ontario"
        description="Professional general contracting and construction services in Toronto and the Greater Toronto Area. Specializing in commercial, residential, and industrial construction projects."
        services={[
          "General Contracting Toronto",
          "Commercial Construction Toronto", 
          "Residential Construction Toronto",
          "Condo Renovations Toronto",
          "Office Construction Toronto"
        ]}
      />
      <SchemaMarkup type="FAQ" data={faqData} />
      
      <Header />
      <Breadcrumbs />
      
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-secondary py-24 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <Badge variant="secondary" className="mb-4 bg-white/10 text-white border-white/20">
                  Toronto General Contracting
                </Badge>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                  General Contracting & Construction in Toronto
                </h1>
                <p className="text-xl lg:text-2xl mb-8 text-white/90 leading-relaxed">
                  {cityData.intro}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                    <Link to="/contact">Get Toronto Quote</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                    <a href="tel:+16477608854">Call: (647) 760-8854</a>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-6 text-white/80">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>Population: {cityData.population}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    <span>{cityData.region}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    <span>Licensed & Insured</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={torontoConstruction} 
                  alt="Toronto skyline with active construction site" 
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Featured Services */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Toronto Construction Services</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Professional Construction Services in Toronto
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From downtown office construction to residential renovations, we deliver exceptional results across the Greater Toronto Area.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {constructionServices.map((service, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <service.icon className="h-8 w-8 text-primary" />
                      </div>
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-base">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Services List */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Our Specialties</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Featured Toronto Construction Services
              </h2>
              <p className="text-xl text-muted-foreground">
                Specialized construction services tailored for Toronto's unique urban environment.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cityData.featured_services.map((service, index) => (
                <Card key={index} className="group hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardContent className="p-6 text-center">
                    <div className="p-3 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                      <CheckCircle className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{service}</h3>
                    <p className="text-muted-foreground text-sm">
                      Professional {service.toLowerCase()} services in Toronto and the GTA.
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us for Toronto Projects */}
        <section className="py-20 bg-gradient-to-b from-secondary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Why Choose Blue Knight</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Toronto's Trusted Construction Partner
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Local expertise, proven results, and commitment to excellence in every Toronto construction project.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <MapPin className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Local Expertise</h3>
                <p className="text-muted-foreground">Deep knowledge of Toronto building codes, permits, and regulations.</p>
              </Card>
              
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Award className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Proven Track Record</h3>
                <p className="text-muted-foreground">15+ years of successful projects across the Greater Toronto Area.</p>
              </Card>
              
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Clock className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">On-Time Delivery</h3>
                <p className="text-muted-foreground">Reliable project completion within budget and timeline.</p>
              </Card>
              
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Fully Licensed</h3>
                <p className="text-muted-foreground">Licensed, bonded, and insured for all Toronto construction work.</p>
              </Card>
            </div>
          </div>
        </section>

        {/* Nearby Service Areas */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">GTA Service Areas</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Serving Toronto and Surrounding Areas
              </h2>
              <p className="text-xl text-muted-foreground">
                Professional construction services throughout the Greater Toronto Area.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <Card className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-primary/5">
                  <div className="flex items-center justify-center gap-2">
                    <Star className="h-4 w-4 text-primary" />
                    <span className="font-semibold">Toronto</span>
                  </div>
                </Card>
                {cityData.nearby_areas.map((area, index) => (
                  <Card key={index} className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="font-medium">{area}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Project Gallery */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Toronto Projects</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Recent Toronto Construction Projects
              </h2>
              <p className="text-xl text-muted-foreground">
                See our construction expertise in action across Toronto and the GTA.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={torontoConstruction} 
                    alt="Toronto downtown office construction" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Downtown Office Tower</h3>
                    <p className="text-sm text-white/80">Financial District, Toronto</p>
                  </div>
                </div>
              </Card>
              
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={residentialConstruction} 
                    alt="Toronto condo renovation project" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Luxury Condo Renovation</h3>
                    <p className="text-sm text-white/80">King West, Toronto</p>
                  </div>
                </div>
              </Card>
              
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={generalContractingProjects} 
                    alt="Toronto commercial build-out" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Commercial Build-out</h3>
                    <p className="text-sm text-white/80">Entertainment District</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Frequently Asked Questions</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Toronto Construction FAQ
              </h2>
              <p className="text-xl text-muted-foreground">
                Common questions about construction services in Toronto and the GTA.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto grid gap-6">
              {faqData.map((faq, index) => (
                <Card key={index} className="border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-left text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary via-primary-dark to-secondary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Ready to Start Your Toronto Construction Project?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                {cityData.cta} Get professional construction services from Toronto's trusted general contractors.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Phone className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Call Us</h3>
                  <p className="text-white/80">
                    <a href="tel:+16477608854" className="hover:text-white transition-colors">
                      (647) 760-8854
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Mail className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Email Us</h3>
                  <p className="text-white/80">
                    <a href="mailto:info@blueknightcontracting.ca" className="hover:text-white transition-colors">
                      info@blueknightcontracting.ca
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <MapPin className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Service Area</h3>
                  <p className="text-white/80">Toronto & GTA</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to="/contact">Get Toronto Construction Quote</Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                  <Link to="/gallery">View Our Toronto Projects</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default Toronto;